import React, { useEffect, useState } from 'react';

import Select from '@atlaskit/select';
import Tooltip from '@atlaskit/tooltip';

import { Label } from '@qality/common-components';
import { TestTypeIcon } from '@qality/common-components/src/molecules/TestTypeComponent';
import TestTypeColorUtil from '@qality/common-components/src/molecules/TestTypeComponent/TestTypeColor.util';
import { ValueContainerWithOverflow } from '@qality/common-components/src/select/select-components/ValueContainerWithOverflow';
import { FEATURE_FLAG } from '@qality/common-config';
import { TestType } from '@qality/common-model';
import { useOverflowObserver } from '@qality/common-utils';

import {
  HEADING_AND_MESSAGES_STYLE,
  TestTypeMultiSelectStyled,
  TestTypeOptionIconStyled,
  TestTypeOptionLabelStyled,
  TestTypeOptionWithIconStyled,
} from './TestTypeMultiSelect.styled';
import {
  filterOutAllTestTypesOption,
  getTestTypeOptions,
  mapSelectOptionsToTestTypes,
  mapTestTypesToSelectOptions,
} from './TestTypeMultiSelect.util';

export type TestTypeOption = {
  label: string;
  value: TestType;
};

export type Props = {
  testTypes: Array<TestType>;
  isTestTypesLoaded: boolean;
  selectedTestTypes: Array<TestType>;
  onChange: (testType: Array<TestType>) => void;
  tooltipContent?: string;
  optionsLimit?: number;
  minWidth?: number;
  showLabel?: boolean;
  spacing?: 'compact' | 'default';
};

export const ALL_TEST_TYPES = {
  label: 'All test types',
  value: null,
};

export default function TestTypeMultiSelect({
  testTypes,
  isTestTypesLoaded,
  selectedTestTypes,
  onChange,
  tooltipContent = '',
  minWidth,
  showLabel = true,
  spacing = 'default',
}: Props): JSX.Element {
  const [testTypeOptions, setTestTypeOptions] = useState<Array<TestTypeOption>>(
    getTestTypeOptions(testTypes)
  );

  useEffect(() => {
    if (isTestTypesLoaded) {
      const newTestTypeOptions = getTestTypeOptions(testTypes);
      setTestTypeOptions(newTestTypeOptions);
    }
  }, [testTypes, isTestTypesLoaded]);

  const getAvailableToPickTestTypeOptions = (): TestTypeOption[] => {
    const selectedOptions = mapTestTypesToSelectOptions(selectedTestTypes);
    const remainingAvailableOptions = testTypeOptions.filter((option) => {
      return !selectedOptions.find(
        (selectedOption) => selectedOption.label === option.label
      );
    });

    return filterOutAllTestTypesOption(remainingAvailableOptions);
  };

  const onTestTypeOptionsChange = (options: Array<TestTypeOption>) => {
    if (options.length === 0 || options[options.length - 1].value === null) {
      onChange([]);
    } else {
      const filteredTestTypesOptions = filterOutAllTestTypesOption(options);
      onChange(mapSelectOptionsToTestTypes(filteredTestTypesOptions));
    }
  };

  const getCurrentTestTypeValue = (): Array<TestTypeOption> => {
    const currentlySelectedOptions =
      mapTestTypesToSelectOptions(selectedTestTypes);
    return currentlySelectedOptions.length === 0
      ? [ALL_TEST_TYPES]
      : currentlySelectedOptions;
  };

  const TestTypeOptionWithIcon = ({ label, value }: TestTypeOption) => {
    const testType = value;
    const { ref, isOverflowing } = useOverflowObserver();

    return (
      <Tooltip content={isOverflowing && label}>
        <TestTypeOptionWithIconStyled>
          {label !== ALL_TEST_TYPES.label && (
            <TestTypeOptionIconStyled
              backgroundColor={TestTypeColorUtil.getBackgroundColor(
                'subtle',
                testType.colorName
              )}
            >
              <TestTypeIcon
                iconName={testType.iconName}
                colorName={testType.colorName}
                appearance='subtle'
                size='small'
                label='test-type-icon'
              />
            </TestTypeOptionIconStyled>
          )}
          <TestTypeOptionLabelStyled innerRef={ref}>
            {label}
          </TestTypeOptionLabelStyled>
        </TestTypeOptionWithIconStyled>
      </Tooltip>
    );
  };

  return (
    <TestTypeMultiSelectStyled minWidth={minWidth}>
      {showLabel && <Label text='Test type' />}
      <Tooltip content={tooltipContent}>
        <Select
          isMulti
          id='test-type-picker'
          defaultValue={ALL_TEST_TYPES}
          value={getCurrentTestTypeValue()}
          options={getAvailableToPickTestTypeOptions()}
          onChange={onTestTypeOptionsChange}
          styles={{
            noOptionsMessage: HEADING_AND_MESSAGES_STYLE,
            option: (provided) => ({
              ...provided,
              overflow: 'hidden',
            }),
          }}
          isLoading={!isTestTypesLoaded}
          isDisabled={!FEATURE_FLAG.isPremium}
          components={{
            ValueContainer: ValueContainerWithOverflow,
          }}
          formatOptionLabel={(option: TestTypeOption) => (
            <TestTypeOptionWithIcon {...option} />
          )}
          spacing={spacing}
        />
      </Tooltip>
    </TestTypeMultiSelectStyled>
  );
}
